// AGC: SubscriptionPage - Main subscription management screen
// AGC: Shows current plan, usage, and upgrade options
// AGC: Includes payment simulation for PRO/PREMIUM upgrades

import React, { useState } from 'react'
import {
  Gift,
  Star,
  Crown,
  ArrowLeft,
  Settings,
  CreditCard,
  Calendar,
  RefreshCw,
  ChevronRight,
  Zap,
  TrendingUp,
  Ticket,
  Check,
  Loader2,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { useSubscription } from '../../context/SubscriptionContext'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'
import { UsageDashboard } from './UsageDashboard'
import { PricingCards } from './PricingCards'
import { TierComparison } from './TierComparison'
import { UpgradeModal } from './UpgradeModal'
import { PaymentSimulation } from './PaymentSimulation'
import {
  type SubscriptionTier,
  TIER_DISPLAY_INFO,
  TIER_PRICING,
  getNextTier,
} from '../../types/subscription'
import type { Screen } from '../../types'
import SorteioEntryModal from '../sorteio/SorteioEntryModal'
import type { SorteioEntryModalData } from '../../types/sorteio'

interface SubscriptionPageProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: Icon component for tiers
 */
const TierIcon: React.FC<{ tier: SubscriptionTier; className?: string }> = ({ tier, className = 'w-8 h-8' }) => {
  switch (tier) {
    case 'free':
      return <Gift className={className} />
    case 'pro':
      return <Star className={className} />
    case 'premium':
      return <Crown className={className} />
  }
}

/**
 * AGC: Tab type for page sections
 */
type Tab = 'overview' | 'plans' | 'compare'

/**
 * AGC: Subscription Page Component
 */
export const SubscriptionPage: React.FC<SubscriptionPageProps> = ({
  onBack,
}) => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  const {
    tier,
    subscription,
    isLoading,
    refreshSubscription,
    daysUntilReset,
    resetDateString,
  } = useSubscription()

  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false)

  // AGC: Payment simulation state
  const [showPayment, setShowPayment] = useState(false)
  const [selectedTierForPayment, setSelectedTierForPayment] = useState<'pro' | 'premium' | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')

  // AGC: Coupon redemption state
  const [couponCode, setCouponCode] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null)

  // AGC: Sorteio entry modal state — shown when redeemed coupon includes a raffle
  const [showSorteioModal, setShowSorteioModal] = useState(false)
  const [sorteioModalData, setSorteioModalData] = useState<SorteioEntryModalData | null>(null)

  // AGC: Redeem a coupon code
  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return
    setCouponLoading(true)
    setCouponError(null)
    setCouponSuccess(null)

    try {
      const token = await getAuthToken()
      const response = await fetch(`${getBackendUrl()}/api/v1/coupons/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code: couponCode.trim() }),
      })

      const data = await response.json()

      if (data.success) {
        const r = data.redemption
        const expiryDate = r.expiresAt ? new Date(r.expiresAt).toLocaleDateString('pt-BR') : ''
        setCouponSuccess(
          t('subscription.coupon.successMessage')
            ?.replace('{tier}', (r.targetTier || '').toUpperCase())
            ?.replace('{months}', String(r.durationMonths || ''))
            ?.replace('{date}', expiryDate)
          || `${(r.targetTier || '').toUpperCase()} ativado! Válido até ${expiryDate}`
        )
        setCouponCode('')
        await refreshSubscription()

        // AGC: If the redeemed coupon has a sorteio, show the entry modal
        if (data.sorteio) {
          setSorteioModalData(data.sorteio as SorteioEntryModalData)
          setShowSorteioModal(true)
        }
      } else {
        setCouponError(data.error || t('subscription.coupon.errorInvalid'))
      }
    } catch (err) {
      setCouponError(t('subscription.coupon.errorInvalid') || 'Código inválido')
    } finally {
      setCouponLoading(false)
    }
  }

  const display = TIER_DISPLAY_INFO[tier]
  const pricing = TIER_PRICING[tier]
  const nextTier = getNextTier(tier)
  const nextTierDisplay = nextTier ? TIER_DISPLAY_INFO[nextTier] : null

  const handleRefresh = async () => {
    await refreshSubscription()
  }

  // AGC: Handle tier selection - show payment for PRO/PREMIUM
  const handleTierSelect = (selectedTier: SubscriptionTier) => {
    if (selectedTier === tier) return // AGC: Same tier, no action
    
    if (selectedTier === 'free') {
      // AGC: Downgrade to free - no payment needed
      // AGC: In production, show confirmation modal
      console.log('📋 AGC: Downgrading to FREE tier')
    } else {
      // AGC: PRO or PREMIUM - show payment simulation
      setSelectedTierForPayment(selectedTier)
      setShowPayment(true)
    }
  }

  // AGC: Handle successful payment
  const handlePaymentSuccess = async (result: { success: boolean; tier: string; transactionId: string }) => {
    console.log('✅ AGC: Payment successful:', result)
    setShowPayment(false)
    setSelectedTierForPayment(null)
    
    // AGC: Refresh subscription to get updated tier
    await refreshSubscription()
    
    // AGC: Show success message
    setShowUpgradeSuccess(true)
    setTimeout(() => setShowUpgradeSuccess(false), 5000)
  }

  // AGC: Handle payment cancellation
  const handlePaymentCancel = () => {
    setShowPayment(false)
    setSelectedTierForPayment(null)
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: t('subscription.overview') || 'Visão Geral', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'plans', label: t('subscription.plans') || 'Planos', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'compare', label: t('subscription.compare') || 'Comparar', icon: <Settings className="w-4 h-4" /> },
  ]

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('subscription.title') || 'Assinatura'}
        onBack={onBack}
        actions={
          <button 
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* AGC: Current Plan Header */}
      <div className={`${display.bgColor} px-4 py-6`}>
        <div className="flex items-center gap-4">
          <div className={`p-4 rounded-2xl bg-white/30 dark:bg-slate-800/30`}>
            <TierIcon tier={tier} className={`w-10 h-10 ${display.color}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {display.name}
              </h2>
              <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${display.bgColor} ${display.color} border ${display.borderColor}`}>
                {display.badge}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {pricing.monthly === 0 
                ? (t('subscription.freeForever') || 'Gratuito para sempre')
                : `R$ ${pricing.monthly}/mês`
              }
            </p>
            {subscription?.startDate && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                {t('subscription.memberSince') || 'Membro desde'} {new Date(subscription.startDate).toLocaleDateString('pt-BR')}
              </p>
            )}
          </div>
        </div>

        {/* AGC: Quick upgrade CTA */}
        {nextTier && nextTierDisplay && (
          <button
            onClick={() => setActiveTab('plans')}
            className="mt-4 w-full flex items-center justify-between p-3 bg-white/50 dark:bg-slate-800/50 rounded-xl hover:bg-white/70 dark:hover:bg-slate-800/70 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Zap className={`w-5 h-5 ${nextTierDisplay.color}`} />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('subscription.upgradeToUnlock') || 'Faça upgrade para desbloquear mais'}
              </span>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </button>
        )}
      </div>

      {/* AGC: Coupon Code Input */}
      <div className="px-4 py-3">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="w-5 h-5 text-emerald-600" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('subscription.coupon.title') || 'Código Promocional'}
            </h3>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null) }}
              placeholder={t('subscription.coupon.enterCode') || 'Digite o código'}
              maxLength={30}
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 uppercase tracking-wider"
              onKeyDown={(e) => e.key === 'Enter' && handleRedeemCoupon()}
            />
            <button
              onClick={handleRedeemCoupon}
              disabled={couponLoading || !couponCode.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            >
              {couponLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>{t('subscription.coupon.activate') || 'Ativar'}</>
              )}
            </button>
          </div>
          {couponError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">{couponError}</p>
          )}
          {couponSuccess && (
            <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <Check className="w-3.5 h-3.5" />
              <span>{couponSuccess}</span>
            </div>
          )}
          {subscription?.couponCode && subscription?.couponExpiresAt && subscription.status !== 'past_due' && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('subscription.coupon.premiumVia')?.replace('{code}', subscription.couponCode) || `Premium via ${subscription.couponCode}`}
              {' — '}
              {t('subscription.coupon.validUntil')?.replace('{date}', new Date(subscription.couponExpiresAt).toLocaleDateString('pt-BR')) || `Válido até ${new Date(subscription.couponExpiresAt).toLocaleDateString('pt-BR')}`}
            </p>
          )}
          {subscription?.status === 'past_due' && (
            <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                {t('subscription.pastDue.title') || 'Período promocional encerrado'}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                {t('subscription.pastDue.message') || 'Assine para continuar usando os recursos premium.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AGC: Tab Navigation */}
      <div className="px-4 py-3 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="flex gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${activeTab === tab.id
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* AGC: Tab Content */}
      <div className="px-4 py-6 pb-24">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* AGC: Usage Dashboard */}
            <UsageDashboard />

            {/* AGC: Reset Info */}
            <GlassCard>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('subscription.usageResets') || 'Uso mensal renova em'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {daysUntilReset} {t('subscription.days') || 'dias'} ({resetDateString})
                  </p>
                </div>
              </div>
            </GlassCard>

            {/* AGC: Quick Actions */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {t('subscription.quickActions') || 'Ações Rápidas'}
              </h3>
              
              {nextTier && (
                <button
                  onClick={() => setActiveTab('plans')}
                  className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  <Zap className="w-6 h-6" />
                  <div className="flex-1 text-left">
                    <p className="font-semibold">
                      {t('subscription.upgradeNow') || 'Fazer Upgrade'}
                    </p>
                    <p className="text-sm text-white/80">
                      {t('subscription.unlockMore') || 'Desbloqueie mais recursos'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={() => setActiveTab('compare')}
                className="w-full flex items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Settings className="w-6 h-6 text-gray-400" />
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-700 dark:text-gray-300">
                    {t('subscription.comparePlans') || 'Comparar Planos'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('subscription.seeAllFeatures') || 'Veja todos os recursos'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('subscription.choosePlan') || 'Escolha seu Plano'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('subscription.planDescription') || 'Selecione o plano ideal para sua fazenda'}
              </p>
            </div>
            
            {/* AGC: Billing cycle toggle */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    billingCycle === 'monthly'
                      ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Mensal
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    billingCycle === 'annual'
                      ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  Anual
                  <span className="ml-1 text-xs text-emerald-500 font-bold">-17%</span>
                </button>
              </div>
            </div>
            
            <PricingCards 
              showCurrent={true}
              onSelectTier={handleTierSelect}
            />

            {/* AGC: Annual savings info */}
            <GlassCard>
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  💡 {t('subscription.annualSavings') || 'Economize 17% com o plano anual!'}
                </p>
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'compare' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('subscription.compareTitle') || 'Compare os Planos'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('subscription.compareDescription') || 'Veja o que cada plano oferece'}
              </p>
            </div>
            
            <TierComparison showCategories={true} />
          </div>
        )}
      </div>

      {/* AGC: Upgrade Modal */}
      <UpgradeModal />

      {/* AGC: Success Toast - safe-area-bottom keeps it above BottomNav + Android nav bar */}
      {showUpgradeSuccess && (
        <div
          className="fixed left-4 right-4 bg-emerald-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-in slide-in-from-bottom duration-300"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
        >
          <span className="text-xl">🎉</span>
          <p className="font-medium">
            {t('subscription.upgradeSuccess') || 'Plano atualizado com sucesso!'}
          </p>
        </div>
      )}

      {/* AGC: Payment Simulation Modal */}
      {showPayment && selectedTierForPayment && (
        <PaymentSimulation
          tier={selectedTierForPayment}
          billingCycle={billingCycle}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
          onBack={handlePaymentCancel}
        />
      )}

      {/* AGC: Sorteio Entry Modal — shown after coupon redemption when hasSorteio is true */}
      {showSorteioModal && sorteioModalData && (
        <SorteioEntryModal
          data={sorteioModalData}
          onClose={() => setShowSorteioModal(false)}
          onGoToTasks={() => {
            setShowSorteioModal(false)
            onNavigate('sorteios')
          }}
        />
      )}
    </div>
  )
}

export default SubscriptionPage

